import { useState, useRef, useEffect } from 'react'
import { Settings2 } from 'lucide-react'
import type { CompetitorColumn } from '@ispot/shared'

const COLUMN_LABELS: Record<CompetitorColumn, string> = {
  type: 'Type',
  url: 'URL',
  pricing: 'Pricing',
  whatTheyDo: 'What They Do',
  strengths: 'Strengths',
  gaps: 'Gaps',
  notes: 'Notes',
  investors: 'Investors',
  hq: 'HQ',
  isAiFirst: 'AI-First',
}

interface ColumnVisibilityToggleProps {
  visibleColumns: Record<string, boolean>
  onChange: (cols: Record<string, boolean>) => void
}

export function ColumnVisibilityToggle({ visibleColumns, onChange }: ColumnVisibilityToggleProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  function toggle(col: string) {
    onChange({ ...visibleColumns, [col]: !visibleColumns[col] })
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          background: 'white',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
          cursor: 'pointer',
        }}
      >
        <Settings2 size={14} />
        Columns
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '36px',
          right: 0,
          background: 'white',
          border: '1px solid var(--color-border)',
          borderRadius: '10px',
          padding: '8px',
          boxShadow: '0 4px 16px rgba(29,26,21,0.12)',
          zIndex: 100,
          minWidth: '160px',
        }}>
          <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)', padding: '4px 8px 8px' }}>
            Visible Columns
          </p>
          {(Object.keys(COLUMN_LABELS) as CompetitorColumn[]).map(col => (
            <label
              key={col}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                color: 'var(--color-text)',
              }}
            >
              <input
                type="checkbox"
                checked={!!visibleColumns[col]}
                onChange={() => toggle(col)}
                style={{ width: '14px', height: '14px', accentColor: 'var(--color-accent)' }}
              />
              {COLUMN_LABELS[col]}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
